// Who in your circles is open to taste together, read from the edge (GET /v1/presence/friends).
// Outside a tasting nothing polls: the list is read when a screen that shows it opens and when the
// app comes back into view, so a row's presence is at most one visit old. Inside a tasting the
// peer mesh carries it live (liveSession.ts).
import type { PresenceEntry } from "@proef/contract";
import { useEffect, useSyncExternalStore } from "react";
import { getAuthState } from "./auth";
import { listPresence } from "./presenceApi";

const EMPTY: ReadonlyMap<string, PresenceEntry> = new Map();
let entries: ReadonlyMap<string, PresenceEntry> = EMPTY;
let inflight: Promise<void> | undefined;
const listeners = new Set<() => void>();

function set(next: ReadonlyMap<string, PresenceEntry>): void {
  entries = next;
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-reads the list; signed out it is empty. A failed read keeps what was there. */
export function refreshPresence(): Promise<void> {
  inflight ??= (async () => {
    const auth = getAuthState();
    try {
      if (auth.status !== "signedIn") {
        if (auth.status === "signedOut") set(EMPTY);
        return;
      }
      const list = await listPresence();
      // A sign-out or switch of account during the read must not hand the old list to the new one.
      const now = getAuthState();
      if (now.status !== "signedIn" || now.user.id !== auth.user.id) return;
      set(new Map(list.items.map((entry) => [entry.userId, entry])));
    } catch {
      // Offline or the edge away: the next visit tries again.
    } finally {
      inflight = undefined;
    }
  })();
  return inflight;
}

/** Everything read so far, keyed by user id. `load` reads it on mount and whenever the app is
 * visible again; a screen that only shows what another already loaded leaves it off. */
export function usePresence(load = false): ReadonlyMap<string, PresenceEntry> {
  useEffect(() => {
    if (!load) return undefined;
    void refreshPresence();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void refreshPresence();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);
  return useSyncExternalStore(
    subscribe,
    () => entries,
    () => EMPTY,
  );
}

/** How many people in the list are tasting right now. */
export function tastingNowCount(list: ReadonlyMap<string, PresenceEntry>): number {
  return [...list.values()].filter((entry) => entry.activity !== undefined).length;
}

/** The people tasting in `sessionId`; the edge names the session's id only to someone who may join,
 * so this counts the ones the viewer can see to be in it. */
export function tastingInSession(
  list: ReadonlyMap<string, PresenceEntry>,
  sessionId: string,
): readonly string[] {
  return [...list.values()]
    .filter((entry) => entry.activity?.sessionId === sessionId)
    .map((entry) => entry.userId);
}

/** Whoever signs out or in starts from an empty list (the beacon calls this on a change of account). */
export function resetPresenceList(): void {
  if (entries !== EMPTY) set(EMPTY);
}
